import dayjs from 'dayjs'
import type { Article, WithContext } from 'schema-dts'

import nbsp from './remove-hanging-conjunctions'
import getSeoSettings from './seo-settings'

import type { PublicArticleDetail } from '@/service/__generated-api'

/**
 * Extracts single element from list or return element itself
 * @param elmOrArray
 */

function flatten<T>(elmOrArray: T | T[]): T {
  return Array.isArray(elmOrArray) ? elmOrArray[0] : elmOrArray
}

/**
 * Create a list from a single element or return the list itself
 * @param elmOrArray
 */
function ensureArray<T>(elmOrArray: T | T[]): T[] {
  return Array.isArray(elmOrArray) ? elmOrArray : [elmOrArray]
}

/**
 * Converts empty string or null to undefined
 * @param str
 */

function emptyToUndefined(str?: string | null): string | undefined {
  return !str || str.length === 0 ? undefined : str
}

/**
 * Repeats an array until the resulting array's
 * length is equal or greater than `minItems`
 * @param items
 * @param minItems
 */

function getItemsForLoop<T>(items: T[], minItems: number) {
  const { length } = items
  const repeats = length >= minItems ? 1 : Math.ceil(minItems / length)
  return Array.from({ length: repeats * length }, (_, i) => items[i % length])
}

/**
 * Extracts text from html
 * @param html
 */

function stripTags(html: string) {
  return html.replace(/<\/?[^>]+(>|$)/g, '')
}

/**
 * Get Schema.org data for a blog post
 * @param page
 */

function getBlogPostLD(page: PublicArticleDetail) {
  const schema: WithContext<Article> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: page.title,
    image: page.image.file,
    datePublished: page.publication_date || undefined,
    keywords: page.meta_keywords,
    description: page.meta_description || page.og_description
  }

  if (page.author) {
    schema.author = {
      '@type': 'Person',
      name: page.author.name
    }
  }

  return JSON.stringify(schema)
}

/**
 * Convert date string to Dayjs object, handle timezone
 * @param dateString
 * @param timezoned
 */

function getDateObject(dateString: string | Date, timezoned = true) {
  return timezoned ? dayjs(dateString).tz('Poland') : dayjs(dateString)
}

type DatetimeFormat = 'DD.MM.YYYY' | 'D.MM.YYYY' | 'D.MM'

/**
 * Format datetime string, handle timezone
 * @param dateString
 * @param format
 * @param timezoned
 */

// eslint-disable-next-line max-params
function formatDate(
  dateString: string | Date,
  format: DatetimeFormat,
  timezoned = true
) {
  return getDateObject(dateString, timezoned).format(format)
}

export {
  flatten,
  ensureArray,
  emptyToUndefined,
  getItemsForLoop,
  nbsp,
  getBlogPostLD,
  stripTags,
  getSeoSettings,
  formatDate,
  getDateObject
}
